import { useLazyQuery } from "@apollo/client";
import _ from "lodash";

import { Button } from "../../../../components/ui";
import { FETCH_ARTWORK_DESCRIPTIONS } from "../../../../graphql//query/Artwork";

export const buildInstanceDescriptionsInput = ({ ids = [], instance }) => {
  if (instance) {
    return {
      id: {
        operator: "eq",
        value: instance.artworkId || instance.edition?.artwork?.id,
      },
    };
  }

  return {
    id: {
      operator: "in",
      value: ids,
    },
    // Explicit pagination avoids API defaults truncating larger selections.
    limit: ids.length || 1,
    offset: 0,
  };
};

const copyTextWithExecCommand = value => {
  if (typeof document === "undefined") return false;

  let textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();
  textArea.setSelectionRange(0, value.length);

  try {
    return typeof document.execCommand === "function"
      ? document.execCommand("copy")
      : false;
  } finally {
    document.body.removeChild(textArea);
    textArea = null;
  }
};

export const copyTextToClipboard = async text => {
  const safeText = text || "";
  const clipboard =
    typeof navigator !== "undefined" ? navigator.clipboard : null;

  if (clipboard?.writeText) {
    try {
      await clipboard.writeText(safeText);
      return true;
    } catch (_error) {
      // Fall back to execCommand for browsers with clipboard API restrictions.
      return copyTextWithExecCommand(safeText);
    }
  }

  return copyTextWithExecCommand(safeText);
};

/**
 * This component fetches instance(s) associated artworks' press descriptions
 * @param {object} props.instance - if opened from instance detail
 * @param {number} props.artworkIds - if opened from instance list
 * @param {function} props.closeModal
 * @returns {JSX.Element}
 */
const CopyPressDescriptionBody = props => {
  const { closeModal, instance, artworkIds } = props;

  const [fetchArtworkDescription, { loading }] = useLazyQuery(
    FETCH_ARTWORK_DESCRIPTIONS(),
  );

  const handleClick = async () => {
    const input = buildInstanceDescriptionsInput({
      ids: _.uniq(artworkIds).filter(Boolean),
      instance,
    });

    const result = await fetchArtworkDescription({ variables: { input } });
    const edges = result?.data?.artworksV2?.edges || [];
    const description = _.join(
      edges.map(({ pressDescription }) => pressDescription),
      "\n",
    );

    await copyTextToClipboard(description);
    closeModal();
  };

  return (
    <div className="mt-4 flex">
      <Button
        className="mr-3"
        label={"Cancel"}
        action={"default"}
        onClick={() => {
          closeModal();
        }}
      />
      <Button
        disabled={loading}
        label={loading ? "Fetching description" : "Copy description"}
        onClick={handleClick}
      />
    </div>
  );
};

export default CopyPressDescriptionBody;
